/**
 * Org-safe facade over the generated resource-grants client.
 *
 * A resource grant shares ONE resource (today: an agent session) with a
 * user, a team, or the whole org for an interval; `expiresAt: null` never
 * lapses. Each hook takes a branded {@link OrgId} so an org-scoped call
 * without an active org is a compile error (see `./org-id.ts`). The org
 * rides on the `x-organization-id` header, so the id's role here is cache
 * partitioning: every key is prefixed with the org.
 */
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import {
  useCreateResourceGrant,
  useListResourceGrants,
  useRevokeResourceGrant,
  useSetResourceGrantExpiry,
} from '../generated/resource-grants';
import type { ListResourceGrantsParams } from '../generated/model/listResourceGrantsParams';
import type { OrgId } from './org-id';

/**
 * The wire shape a grant arrives in. The shared `ResourceGrant` types its
 * ids as prefix-checked template literals for the server boundary; client
 * consumers deal in already-serialized API data, which the generated
 * client types as plain (pattern-documented) strings.
 */
export type { ResourceGrant } from '@runloop/reflex-client/react/model/resourceGrant';

export const resourceGrantsKey = (org: OrgId, params?: ListResourceGrantsParams) =>
  params ? (['resource-grants', org, params] as const) : (['resource-grants', org] as const);

/**
 * List the grants visible to the caller. `org` is required at compile time.
 * The previous page is kept while a params change (e.g. toggling
 * `includeEnded`) refetches, so filter toggles don't blank the list.
 */
export function useResourceGrants(org: OrgId, params?: ListResourceGrantsParams) {
  return useListResourceGrants(params, {
    query: { queryKey: resourceGrantsKey(org, params), placeholderData: keepPreviousData },
  });
}

/** Share a resource (idempotent ensure), invalidating the org's grant lists. */
export function useCreateResourceGrantMutation(org: OrgId) {
  const qc = useQueryClient();
  return useCreateResourceGrant({
    mutation: {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ['resource-grants', org] }),
    },
  });
}

/** Revoke a grant (end its interval now), invalidating the org's grant lists. */
export function useRevokeResourceGrantMutation(org: OrgId) {
  const qc = useQueryClient();
  return useRevokeResourceGrant({
    mutation: {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ['resource-grants', org] }),
    },
  });
}

/** Move a grant's expiry (later, earlier-but-future, or null = never). */
export function useSetResourceGrantExpiryMutation(org: OrgId) {
  const qc = useQueryClient();
  return useSetResourceGrantExpiry({
    mutation: {
      onSuccess: () => void qc.invalidateQueries({ queryKey: ['resource-grants', org] }),
    },
  });
}
